import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import client from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/StatusBadge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Plus, Loader2, Copy, ShieldCheck, Check, Ban, CheckCircle2, KeyRound } from 'lucide-react';

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [roles, setRoles] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const isDeveloper = user?.role === 'developer';
  const canManageUsers = ['developer', 'company_admin'].includes(user?.role);

  const load = () => {
    client.get('/users').then((r) => setUsers(r.data)).catch(() => setUsers([]));
    client.get('/roles').then((r) => setRoles(r.data)).catch(() => {});
    if (canManageUsers) client.get('/invites').then((r) => setInvites(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // company_admin cannot assign/modify the developer role
  const roleOptions = roles.filter((r) => isDeveloper || r.role !== 'developer');

  const changeRole = async (uid, role) => { try { await client.post(`/users/${uid}/role`, { role }); toast.success('Role updated'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const setStatus = async (uid, status) => { try { await client.post(`/users/${uid}/status`, { status }); toast.success(status === 'disabled' ? 'User deactivated' : 'User activated'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const approve = async (uid) => { try { await client.post(`/users/${uid}/approve`); toast.success('Company admin approved'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };

  const pendingCount = (users || []).filter((u) => u.status === 'pending_approval').length;

  return (
    <AppShell title="Users & Roles">
      <Tabs defaultValue="users">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="users" data-testid="users-tab">Users{pendingCount > 0 && <span className="ml-1.5 rounded-full bg-[hsl(var(--gold))] px-1.5 text-[10px] font-bold text-[hsl(var(--foreground))]">{pendingCount}</span>}</TabsTrigger>
            <TabsTrigger value="roles" data-testid="roles-tab">Roles</TabsTrigger>
            {canManageUsers && <TabsTrigger value="invites" data-testid="invites-tab">Invitations</TabsTrigger>}
          </TabsList>
          {canManageUsers && <div className="flex gap-2"><Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-invite-button"><Plus className="mr-1.5 h-4 w-4" />Bulk Invite</Button><Button onClick={() => setInviteOpen(true)} data-testid="invite-user-button"><Plus className="mr-1.5 h-4 w-4" />Invite User</Button></div>}
        </div>

        <TabsContent value="users">
          <Card className="overflow-hidden">
            {users === null ? <div className="space-y-2 p-4">{[1,2,3].map(i=><Skeleton key={i} className="h-12" />)}</div> : (
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Last Login</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>{users.map((u) => {
                  const isDev = u.role === 'developer';
                  const editable = canManageUsers && (isDeveloper || !isDev) && u.user_id !== user?.user_id;
                  return (
                  <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell>{editable ? (
                      <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}><SelectTrigger className="h-8 w-44" data-testid={`user-role-select-${u.user_id}`}><SelectValue /></SelectTrigger><SelectContent>{roleOptions.map((r) => <SelectItem key={r.role} value={r.role}>{r.label}</SelectItem>)}</SelectContent></Select>
                    ) : <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium">{u.role_label}</span>}</TableCell>
                    <TableCell>
                      {u.status === 'pending_approval' ? <StatusBadge status="pending" label="Pending Approval" /> : u.status === 'disabled' ? <StatusBadge status="terminated" label="Disabled" /> : <StatusBadge status="active" label="Active" />}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.last_login ? new Date(u.last_login).toLocaleDateString() : '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {isDeveloper && u.status === 'pending_approval' && (
                          <Button size="sm" variant="outline" className="h-8 border-[hsl(var(--success))]/40 text-[hsl(var(--success))]" onClick={() => approve(u.user_id)} data-testid={`user-approve-${u.user_id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Approve</Button>
                        )}
                        {editable && u.status !== 'pending_approval' && (
                          u.status === 'disabled'
                            ? <Button size="sm" variant="outline" className="h-8" onClick={() => setStatus(u.user_id, 'active')} data-testid={`user-activate-${u.user_id}`}><Check className="mr-1 h-3.5 w-3.5" />Activate</Button>
                            : <Button size="sm" variant="outline" className="h-8 border-[hsl(var(--danger))]/40 text-[hsl(var(--danger))]" onClick={() => setStatus(u.user_id, 'disabled')} data-testid={`user-disable-${u.user_id}`}><Ban className="mr-1 h-3.5 w-3.5" />Deactivate</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ); })}</TableBody>
              </Table></div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="roles">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {roles.map((r) => (
              <Card key={r.role} className="p-5"><div className="mb-2 flex items-center gap-2 font-display font-semibold"><ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))]" />{r.label}</div>
                <ul className="space-y-1">{r.permissions.map((p) => <li key={p} className="flex items-center gap-1.5 text-sm text-muted-foreground"><Check className="h-3.5 w-3.5 text-[hsl(var(--success))]" />{p.replace(/_/g, ' ')}</li>)}</ul>
              </Card>
            ))}
          </div>
        </TabsContent>

        {canManageUsers && (
        <TabsContent value="invites">
          <Card className="overflow-hidden">
            {invites.length === 0 ? <div className="p-12 text-center text-sm text-muted-foreground">No invitations sent yet.</div> : (
              <div className="overflow-x-auto"><Table><TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Code</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Link</TableHead></TableRow></TableHeader>
                <TableBody>{invites.map((inv) => (
                  <TableRow key={inv.token}>
                    <TableCell>{inv.email}</TableCell>
                    <TableCell className="capitalize">{(inv.role || '').replace('_', ' ')}</TableCell>
                    <TableCell>
                      {inv.code ? <button className="flex items-center gap-1 font-mono text-xs font-semibold text-[hsl(var(--primary))] hover:underline" onClick={() => { navigator.clipboard.writeText(inv.code); toast.success('Code copied'); }} data-testid={`copy-code-${inv.token}`}><KeyRound className="h-3 w-3" />{inv.code}</button> : '-'}
                    </TableCell>
                    <TableCell><span className="capitalize">{inv.status}</span></TableCell>
                    <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/signup?token=${inv.token}`); toast.success('Invite link copied'); }} data-testid={`copy-invite-${inv.token}`}><Copy className="mr-1.5 h-3.5 w-3.5" />Copy Link</Button></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table></div>
            )}
          </Card>
        </TabsContent>)}
      </Tabs>
      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} roles={roleOptions} onAdded={load} />
      <BulkInviteDialog open={bulkOpen} onOpenChange={setBulkOpen} roles={roleOptions} onAdded={load} />
    </AppShell>
  );
}

function BulkInviteDialog({ open, onOpenChange, roles, onAdded }) {
  const [entries, setEntries] = useState('');
  const [role, setRole] = useState('driver');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const submit = async () => {
    if (!entries.trim()) { toast.error('Paste at least one email'); return; }
    setLoading(true);
    try { const r = await client.post('/invites/bulk', { entries, role }); setResults(r.data); toast.success(`${r.data.count} invitation(s) created`); onAdded(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setResults(null); setEntries(''); } }}>
      <DialogContent data-testid="bulk-invite-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Bulk Invite</DialogTitle></DialogHeader>
        {results ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{results.count} invitation(s) created. Each invitee gets an email with a unique code &amp; link.</p>
            <div className="max-h-60 space-y-1 overflow-y-auto">{results.results.map((r, i) => (
              <div key={i} className="flex items-center justify-between rounded border p-2 text-xs"><span>{r.email || r.line}</span><span className={r.status==='invited' ? 'font-mono text-[hsl(var(--primary))]' : 'text-muted-foreground'}>{r.status==='invited' ? (r.code || 'invited') : `skipped · ${r.reason||''}`}</span></div>
            ))}</div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Paste one entry per line. Formats: <code>email@x.com</code>, <code>Name &lt;email@x.com&gt;</code>, or <code>Name, email@x.com</code>.</p>
            <div><Label>Role for all</Label><Select value={role} onValueChange={setRole}><SelectTrigger className="mt-1" data-testid="bulk-role-select"><SelectValue /></SelectTrigger><SelectContent>{roles.map((r) => <SelectItem key={r.role} value={r.role}>{r.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Entries</Label><textarea value={entries} onChange={(e) => setEntries(e.target.value)} rows={7} className="mt-1 w-full rounded-lg border bg-white p-2 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" placeholder={"John Doe <john@example.com>\njane@example.com\nMike Ross, mike@example.com"} data-testid="bulk-entries-input" /></div>
          </div>
        )}
        <DialogFooter>{results ? <Button onClick={() => { onOpenChange(false); setResults(null); setEntries(''); }}>Done</Button> : (<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={loading} data-testid="bulk-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send Invites</Button></>)}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteDialog({ open, onOpenChange, roles, onAdded }) {
  const [f, setF] = useState({ email: '', name: '', role: 'driver' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (!f.email || !f.name) { toast.error('Name and email required'); return; }
    setLoading(true);
    try { const r = await client.post('/invites', f); const url = `${window.location.origin}/signup?token=${r.data.invite.token}`; setResult({ url, code: r.data.code, emailed: r.data.emailed }); toast.success('Invitation created'); onAdded(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setResult(null); }}>
      <DialogContent data-testid="invite-dialog"><DialogHeader><DialogTitle>Invite User</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{result.emailed ? 'An email with the code & link was sent.' : 'Share the invitation code and link below with the user.'}</p>
            <div className="rounded-lg border bg-muted/40 p-3 text-center">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Invitation Code</div>
              <button className="mt-1 font-mono text-2xl font-bold tracking-widest text-[hsl(var(--primary))]" onClick={() => { navigator.clipboard.writeText(result.code); toast.success('Code copied'); }} data-testid="invite-code-value">{result.code}</button>
            </div>
            <div className="flex items-center gap-2"><Input value={result.url} readOnly className="text-xs" data-testid="invite-link-value" /><Button size="icon" onClick={() => { navigator.clipboard.writeText(result.url); toast.success('Copied'); }}><Copy className="h-4 w-4" /></Button></div>
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label>Full Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" data-testid="invite-name-input" /></div>
            <div><Label>Email *</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1" data-testid="invite-email-input" /></div>
            <div><Label>Role</Label><Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger className="mt-1" data-testid="invite-role-select"><SelectValue /></SelectTrigger><SelectContent>{roles.map((r) => <SelectItem key={r.role} value={r.role}>{r.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
        )}
        <DialogFooter>{result ? <Button onClick={() => { onOpenChange(false); setResult(null); }}>Done</Button> : (<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={loading} data-testid="invite-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create Invite</Button></>)}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
